import React, { Component, Fragment } from 'react';
import {
  Modal, Button, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import 'react-tagsinput/react-tagsinput.css';
import './AddCoworkersModal.scss';
import RoleChangeDropdown from './RoleChangeDropdown';
import AddCoworkersInput from '../common/AddCoworkersInput';
import CopyLink from '../common/CopyLink';
import { postRequest, putRequest, deleteRequest } from '../../requestUtils';
import userIcon from '../../../assets/images/user-icon.svg';
import copyLinkImg from '../../../assets/images/link-green.svg';
import { analyticsTrack } from '../../analyticsUtils';

const messagePlaceholder = 'Write a message to your colleagues';

// default role changes based on current user.
const defaultRoleSelection = (currentUser) => {
  if (currentUser === null) return 'admin'; // Means currentUser is a rep.

  const level = currentUser.permissions_level;

  if (level === 'owner') { return 'admin'; }
  return level;
};

const _isCurrentUser = (teamMember, currentUser) => currentUser && teamMember.permissions_object_id === currentUser.permissions_object_id;

const buildTeamMembersList = (teamMembers, currentUser) => {
  if (currentUser === null) {
    return teamMembers;
  }

  const worklist = teamMembers.filter((teamMember) => !_isCurrentUser(teamMember, currentUser));
  worklist.push(currentUser);

  return worklist;
};

const renderErrorsList = (errorList) => (
  <div>
    {errorList.map((errorMessage, index) => (
      <div
        className="mt-8 error-messages"
        key={`${errorMessage[0]}${index}`}
      >
        <i className="me-3 fas fa-exclamation-triangle" />
        {errorMessage}
      </div>
    ))}
  </div>
);

class AddCoworkersModal extends Component {
  constructor(props) {
    super(props);

    const membersSerializer = this.props.initialTeamMembers;

    const initCurrentUser = membersSerializer.current_user_permission;
    const teamMembers = buildTeamMembersList(membersSerializer.team_members, initCurrentUser);

    this.state = {
      teamMembers,
      showModal: props.modalIsOpen,
      emails: [],
      optionalMessage: '',
      selectedInvitationRole: defaultRoleSelection(initCurrentUser),
      inviteErrors: [],
      roleChangeErrors: [],
      displayMessage: '',
      currentUser: initCurrentUser, // currentUser is set iff the person looking at the modal
      // has permissions for the project. In many cases, reps
      // will not have permissions, in which case currentUser
      // will be null.
    };

    this.sendInvitations = this.sendInvitations.bind(this);
    this.handleEmailsChange = this.handleEmailsChange.bind(this);
    this.handleOptionalMessageChange = this.handleOptionalMessageChange.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleInvitationRoleChange = this.handleInvitationRoleChange.bind(this);
    this.handleMemberRoleChange = this.handleMemberRoleChange.bind(this);
    this.handleMemberRemoval = this.handleMemberRemoval.bind(this);
    this.handleSendReminder = this.handleSendReminder.bind(this);
    this.handleClearDisplayMessage = this.handleClearDisplayMessage.bind(this);

    this.isCurrentUser = this.isCurrentUser.bind(this);
    this.canSendInvite = this.canSendInvite.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.modalIsOpen && this.props.modalIsOpen) {
      this.handleShowModal();
    }
  }

  onTimesheetPage() {
    return this.props.location === 'timesheet';
  }

  onProjectReviewPage() {
    return this.props.location === 'project-review';
  }

  onProposalPage() {
    return this.props.location === 'proposal';
  }

  isCurrentUser(teamMember) {
    return _isCurrentUser(teamMember, this.state.currentUser);
  }

  canSendInvite() {
    const { emails, selectedInvitationRole } = this.state;

    return emails.length > 0 && selectedInvitationRole.length > 0;
  }

  doesCurrentUserHaveLevel(level) {
    const { currentUser } = this.state;

    return currentUser && currentUser.permissions_level === level;
  }

  isCurrentUserRep() {
    return (this.state.currentUser === null) || this.state.currentUser.is_rep;
  }

  generateSubtitleCopy() {
    if (this.isCurrentUserRep()) {
      return ['As a Clora rep, you can edit the owner of this project from this page.',
        'Inviting a new owner or promoting a current member to owner will automatically demote the current owner to Admin.',
        'If you are the current owner, transferring ownership to someone else will demote you.'];
    } if (this.onTimesheetPage()) {
      return ["Approvers can view, approve, and request changes on this project's timesheets.",
        "Timesheets must be approved within 5 business days of the consultant's submission."];
    } if (this.onProjectReviewPage()) {
      return [
        'Coworkers wil be able to help you during the hiring process.',
        'Invite and allow them to edit the project post, like and dislike',
        'consultants and interview candidates.',
      ];
    } if (this.onProposalPage()) {
      return [`When you share ${this.props.consultantName}'s proposal, your colleague will become a team member for this project.`];
    }

    return ['Ask colleagues to collaborate on this project and review candidates.'];
  }

  headingCopy() {
    if (this.isCurrentUserRep()) {
      return 'Add collaborators to this project';
    } if (this.onProposalPage()) {
      return `Share ${this.props.consultantName}'s Proposal`;
    } if (this.onProjectReviewPage() && this.props.pageSubmitted) {
      return 'Project submitted to Clora for review! Meanwhile add coworkers:';
    }
    return 'Invite to Project';
  }

  generateTooltipCopy() {
    if (this.doesCurrentUserHaveLevel('manager')) {
      return 'As a Manager, you can only grant Manager and Collaborator access levels to your colleagues.';
    } if (this.doesCurrentUserHaveLevel('invoice_approver')) {
      return 'As a Timesheet Approver, you can only grant the Timesheet Approver access level to your colleagues.';
    } if (this.doesCurrentUserHaveLevel('collaborator')) {
      return 'As a Collaborator, you can only grant the Collaborator access level to your colleagues.';
    }

    return null;
  }

  handleClearDisplayMessage() {
    setTimeout(() => {
      this.setState({ displayMessage: '' });
    }, 6000);
  }

  handleHideModal() {
    this.props.hideModalCallback();
    this.setState({
      showModal: false,
      inviteErrors: [],
      roleChangeErrors: [],
      optionalMessage: '',
    });
  }

  handleShowModal() {
    this.props.showModalCallback();
    this.setState({
      showModal: true,
      inviteErrors: [],
      roleChangeErrors: [],
      optionalMessage: '',
    });
  }

  handleEmailsChange(newEmails) {
    this.setState({ emails: newEmails });
  }

  handleOptionalMessageChange(newMessage) {
    this.setState({ optionalMessage: newMessage });
  }

  handleInvitationRoleChange(newRole) {
    this.setState({ selectedInvitationRole: newRole });
  }

  sendInvitations() {
    if (!this.canSendInvite()) {
      return;
    }

    const { emails, optionalMessage, selectedInvitationRole } = this.state;
    const {
      csrfToken, basePermissionsUrl, location, proposalResourceId,
    } = this.props;

    const params = {
      emails,
      level: selectedInvitationRole,
      message: optionalMessage,
      proposal_resource_id: proposalResourceId,
    };

    postRequest(basePermissionsUrl, params, csrfToken).then(
      (response) => {
        const newTeamMembers = buildTeamMembersList(
          response.data.project.team_members,
          response.data.project.current_user_permission,
        );

        analyticsTrack(
          `Sending member invites to ${emails.join(', ')} from team modal`,
          { location: this.onProjectReviewPage() ? 'Project creation' : (location || 'Project View') },
        );

        this.setState(
          {
            emails: [],
            optionalMessage: '',
            selectedInvitationRole: defaultRoleSelection(this.state.currentUser),
            teamMembers: newTeamMembers,
            inviteErrors: response.data.errors,
            roleChangeErrors: [],
            currentUser: response.data.project.current_user_permission,
            displayMessage: `${newTeamMembers.length === 0 ? 'Team' : `${newTeamMembers.length} team`} member successfully added to project`,
          },
        );

        this.handleClearDisplayMessage();
      },
    );
  }

  handleMemberRoleChange(teamMember, newRole) {
    const { csrfToken, basePermissionsUrl } = this.props;
    const projectPermissionId = teamMember.permissions_object_id;
    const url = `${basePermissionsUrl}/${projectPermissionId}`;

    const params = {
      level: newRole,
    };

    putRequest(url, params, csrfToken).then(
      (response) => {
        const newTeamMembers = buildTeamMembersList(
          response.data.project.team_members,
          response.data.project.current_user_permission,
        );
        this.setState(
          {
            teamMembers: newTeamMembers,
            inviteErrors: [],
            roleChangeErrors: response.data.errors,
            currentUser: response.data.project.current_user_permission,
          },
        );
      },
    );
  }

  handleMemberRemoval(teamMember) {
    const { csrfToken, basePermissionsUrl } = this.props;
    const projectPermissionId = teamMember.permissions_object_id;
    const url = `${basePermissionsUrl}/${projectPermissionId}`;

    deleteRequest(url, csrfToken).then(
      ({ data }) => {
        const newTeamMembers = buildTeamMembersList(
          data.project.team_members,
          data.project.current_user_permission,
        );
        this.setState(
          {
            teamMembers: newTeamMembers,
            inviteErrors: [],
            roleChangeErrors: [],
            currentUser: data.project.current_user_permission,
            displayMessage: data.message,
          },
        );

        this.handleClearDisplayMessage();
      },
    );
  }

  handleSendReminder(teamMember, event) {
    const { csrfToken, basePermissionsUrl, proposalResourceId } = this.props;
    const { target } = event;
    const projectPermissionId = teamMember.permissions_object_id;
    const url = `${basePermissionsUrl}/${projectPermissionId}/reminder`;

    postRequest(url, { proposal_resource_id: proposalResourceId }, csrfToken).then(
      () => target.replaceWith(' Reminder sent!'),
    );
  }

  renderAddCoworkersInput() {
    const { invitableRoles } = this.props;
    const {
      optionalMessage, emails, selectedInvitationRole, inviteErrors,
    } = this.state;

    return (
      <AddCoworkersInput
        emails={emails}
        onChangeEmails={this.handleEmailsChange}
        invitableRoles={invitableRoles}
        selectedRole={selectedInvitationRole}
        onChangeSelectedRole={this.handleInvitationRoleChange}
        withMessage
        errors={inviteErrors}
        message={optionalMessage}
        placeholder={messagePlaceholder}
        onChangeMessage={this.handleOptionalMessageChange}
      />
    );
  }

  renderSubmitButton() {
    return (
      <div className="send-invites-button-container">
        <Button
          onClick={this.sendInvitations}
          variant="primary"
          className={`mb-0 submit-invites-button${this.canSendInvite() ? '-populated' : ''}`}
          disabled={!this.canSendInvite()}
        >
          { this.onProposalPage() ? 'Share Proposal' : 'Send invite to collaborate' }
        </Button>
      </div>
    );
  }

  renderAddCoworkersSection() {
    const { copyLink } = this.props;
    const subtitleCopy = this.generateSubtitleCopy();
    const tooltipCopy = this.generateTooltipCopy();

    const tooltip = () => (
      <OverlayTrigger
        placement="right"
        delay={{ show: 100 }}
        overlay={(
          <Tooltip id="access-level-tooltip">
            {tooltipCopy}
          </Tooltip>
        )}
      >
        <i className="ms-3 fas fa-info-circle" />
      </OverlayTrigger>
    );

    return (
      <div className="add-coworkers-modal-content">
        <div className="add-coworkers-header">
          <h2 className="add-coworkers-modal-section-title">
            {this.headingCopy()}
            {tooltipCopy && tooltip()}
          </h2>
          { copyLink
            && (
            <div className="add-coworkers-copy-link">
              <SVG src={copyLinkImg} alt="User Icon" className="user-icon" />
              <CopyLink
                url={copyLink}
                shortForm
                linkText="Copy Link"
              />
            </div>
            )}
        </div>
        {this.onProjectReviewPage() || this.onProposalPage()
          ? <div className="mb-4">{subtitleCopy.join(' ')}</div>
          : (
            <ul className="ps-3 mb-4">
              {subtitleCopy.map((copy) => <li key={copy}>{copy}</li>)}
            </ul>
          )}
        {this.renderAddCoworkersInput()}
        {this.renderSubmitButton()}
      </div>
    );
  }

  // for changing the role of an existing coworker
  renderRoleChangeDropdown(teamMember) {
    const { invitableRoles, editableRoles } = this.props;

    /*
      IMPORTANT DISTINCTION: invitableRoles vs editableRoles

      invitableRoles: the roles I am able to change somebody else to.
      editableRoles: I can edit the permissions of people with these roles.
    */

    /*
       What this mess is trying to say is the current user can change the given team member's role
       if all of these conditions hold:
       1. The team member in question is not an owner (no one is allowed to directly change
          the owner, even reps). Directly changing the owner can lead to bad things happening,
          as we have found out. So we're not even going to let that be a possibility.

       2. The team member in question has a role which can be edited by the current user
          (calculated by user#editable_roles_for and passed in as the prop editableRoles)

       3. The team member in question is not the current user (i.e. you can't edit yourself).
          Reps are exempt from this rule - they can change their own role on a project,
          or even remove themselves.
    */
    const currentUserIsAllowedToChangeRole = teamMember.permissions_level !== 'owner'
        && (editableRoles.includes(teamMember.permissions_level)
          && (!this.isCurrentUser(teamMember) || this.isCurrentUserRep()));

    const onChange = (newRole) => this.handleMemberRoleChange(teamMember, newRole);
    const handleRemove = () => this.handleMemberRemoval(teamMember);

    return (
      <div>
        <RoleChangeDropdown
          permissionOptions={invitableRoles}
          enabled={currentUserIsAllowedToChangeRole}
          onChange={onChange}
          value={teamMember.permissions_level}
          showRemoveOption
          handleRemove={handleRemove}
        />
      </div>
    );
  }

  renderTeamMemberName(teamMember) {
    let text = '';
    if (!teamMember.name) {
      text = teamMember.email;
    } else if (this.isCurrentUser(teamMember)) {
      text = 'You';
    } else {
      text = teamMember.name;
    }

    return (
      <div className="user-name">{text}</div>
    );
  }

  renderTeamMemberEmail(teamMember) {
    if (teamMember.name) {
      return (
        <div className="user-email">{teamMember.email}</div>
      );
    }

    const canSendReminder = this.props.editableRoles.includes(teamMember.permissions_level);

    return (
      <div className="user-email">
        Pending...

        {canSendReminder
          && (
          <a
            tabIndex={0}
            role="button"
            className="send-reminder-button ms-2"
            onClick={(e) => this.handleSendReminder(teamMember, e)}
          >
            Send reminder
          </a>
          )}
      </div>
    );
  }

  renderProjectMembersTable() {
    const { teamMembers } = this.state;

    return (
      <table className="project-members-table" width="100%">
        <tbody>
          <tr className="with-border">
            <th className="first-heading pb-3">Users</th>
            <th>Project access level</th>
          </tr>
          {teamMembers.map((teamMember) => (
            <tr
              className="with-border"
              key={`${teamMember.permissions_object_id}${teamMember.permissions_level}`}
              id={`team-member-entry-${teamMember.email}`}
            >
              <td className="py-3">
                {this.renderTeamMemberName(teamMember)}
                {this.renderTeamMemberEmail(teamMember)}
              </td>
              <td>
                {this.renderRoleChangeDropdown(teamMember)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderProjectMembersSection() {
    const { teamMembers, roleChangeErrors } = this.state;

    return (
      <div className="add-coworkers-modal-content">
        <h2 className="add-coworkers-modal-section-title">
          Hiring Team (
          {teamMembers.length}
          )
        </h2>
        <div className="mt-2 mb-5">
          {renderErrorsList(roleChangeErrors)}
        </div>
        {this.renderProjectMembersTable()}
      </div>
    );
  }

  renderModal() {
    const { invitableRoles, pageSubmitted } = this.props;
    const { displayMessage } = this.state;
    const ableToAddCoworkers = invitableRoles.length > 0;

    return (
      <div>
        <Modal
          show={this.state.showModal}
          onHide={this.handleHideModal}
          dialogClassName="add-coworkers-modal"
          size="lg"
        >
          <div className="add-coworkers-modal-size">
            <Modal.Header closeButton />
            {ableToAddCoworkers
              && (
              <Fragment>
                <Modal.Body className="add-coworkers-modal-body">
                  {this.renderAddCoworkersSection()}
                  {displayMessage && <div className="success-message">{displayMessage}</div>}
                </Modal.Body>
                <hr className="mt-0" />
              </Fragment>
              )}
            <div className="add-coworkers-modal-body">
              {this.renderProjectMembersSection()}
              { pageSubmitted
                && (
                <div className="mt-3 skip-coworkers-container">
                  <a
                    tabIndex={0}
                    role="button"
                    className="skip-coworkers-button"
                    onClick={this.handleHideModal}
                    id="skip-link"
                  >
                    Skip
                  </a>
                </div>
                )}
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  renderMemberIcons() {
    const { teamMembers } = this.state;

    // Render max 3 icons to limit space taken up.
    return (
      <div className="team-member-icons">
        {teamMembers.slice(0, 3).map((teamMember, index) => (
          <div
            className="user-div"
            key={teamMember.permissions_object_id || 0}
            style={{ left: `${index * -5}px` }}
          >
            <div className="user-name">
              {teamMember.name
                ? teamMember.name_initials
                : teamMember.email.charAt(0).toUpperCase()}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderModalButton() {
    const { teamMembers } = this.state;
    const {
      newShareButton, buttonEnabled, location, showNewProposalUX,
    } = this.props;
    const numMembers = teamMembers.length;
    const clickAction = buttonEnabled ? this.handleShowModal : undefined;
    const linkStyle = this.onTimesheetPage() || this.onProjectReviewPage();

    const buttonClass = `mb-0 ${linkStyle ? 'add-coworkers-link' : 'add-coworkers-button'} ${!buttonEnabled ? 'disabled' : ''} ${!newShareButton ? 'msg h-3' : ''}`;

    if (showNewProposalUX) {
      return (
        <div className="add-coworkers-modal-entry  medium-weight">
          <span className="me-3">Hiring Team</span>
          {this.renderMemberIcons()}
        </div>
      );
    }

    const renderActionButton = () => {
      if (location === 'next-steps-component') return null;
      if (newShareButton) {
        return (
          <a
            role="button"
            tabIndex={0}
            onClick={clickAction}
            className="new-share-button"
            disabled={!buttonEnabled}
          >
            <SVG src={userIcon} alt="User Icon" className="user-icon" />
            <span>Collaborators</span>
            <span>
              (
              {numMembers}
              )
            </span>
          </a>
        );
      }

      if (linkStyle) {
        return (
          <a
            role="button"
            tabIndex={0}
            onClick={clickAction}
            className={buttonClass}
            disabled={!buttonEnabled}
          >
            <i className="fa fa-plus ms-5 me-2" />
            Add interviewers
          </a>
        );
      }

      return (
        <Button
          variant="primary"
          onClick={clickAction}
          className={buttonClass}
          disabled={!buttonEnabled}
        >
          Share
        </Button>
      );
    };

    return (
      <div className={`add-coworkers-modal-entry ${linkStyle ? 'left-justify' : ''} medium-weight ${linkStyle ? 'body-text-2' : ''}`}>
        {(!newShareButton && location !== 'next-steps-component') && this.renderMemberIcons()}
        {(!newShareButton && location !== 'next-steps-component') && (
          <a
            role="button"
            tabIndex={0}
            onClick={clickAction}
            className="body-text-2 member-count ms-2"
          >
            {numMembers}
            {' '}
            {numMembers === 1 ? 'member' : 'members'}
          </a>
        )}
        {renderActionButton()}
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderModalButton()}
        {this.renderModal()}
      </div>
    );
  }
}

AddCoworkersModal.propTypes = {
  initialTeamMembers: PropTypes.shape({ team_members: PropTypes.arrayOf(PropTypes.shape({})) }),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  csrfToken: PropTypes.string.isRequired,
  basePermissionsUrl: PropTypes.string.isRequired,
  location: PropTypes.string,
  buttonEnabled: PropTypes.bool,
  showModalCallback: PropTypes.func,
  hideModalCallback: PropTypes.func,
  modalIsOpen: PropTypes.bool,
  pageSubmitted: PropTypes.bool,
  newShareButton: PropTypes.bool,
  consultantName: PropTypes.string,
  proposalResourceId: PropTypes.string,
  copyLink: PropTypes.string,
  showNewProposalUX: PropTypes.bool,
};

AddCoworkersModal.defaultProps = {
  initialTeamMembers: [],
  invitableRoles: [],
  editableRoles: [],
  location: '',
  buttonEnabled: true,
  hideModalCallback: () => {},
  showModalCallback: () => {},
  modalIsOpen: false,
  pageSubmitted: false,
  newShareButton: false,
  consultantName: '',
  proposalResourceId: null,
  copyLink: null,
  showNewProposalUX: false,
};

export default AddCoworkersModal;
